import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
interface PolicyModalProps {
  isOpen: boolean;
  activeTab: 'privacy' | 'terms';
  onClose: () => void;
  onTabChange: (tab: 'privacy' | 'terms') => void;
}
export function PolicyModal({
  isOpen,
  activeTab,
  onClose,
  onTabChange
}: PolicyModalProps) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setShow(true);
      document.body.style.overflow = 'hidden';
    } else {
      const timer = setTimeout(() => setShow(false), 300);
      document.body.style.overflow = 'unset';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen && !show) return null;
  return <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-85">
      <div className={`bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden shadow-2xl relative transition-all duration-300 transform ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`} data-id="element-86">
        {/* Header & Tabs */}
        <div className="flex-none border-b border-zinc-200 bg-zinc-50 pt-2 px-2 md:pt-4 md:px-6 relative" data-id="element-87">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-brand-red hover:bg-zinc-100 rounded-full transition-colors" aria-label="Close modal" data-id="element-88">
            <X className="w-5 h-5" data-id="element-89" />
          </button>

          <div className="flex gap-6 pr-12" data-id="element-90">
            <button onClick={() => onTabChange('privacy')} className={`pb-4 px-2 font-montserrat font-bold text-sm md:text-base transition-colors relative ${activeTab === 'privacy' ? 'text-brand-orange' : 'text-zinc-500 hover:text-zinc-800'}`} data-id="element-91">
              Privacy Policy
              {activeTab === 'privacy' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-orange rounded-t-full" data-id="element-92" />}
            </button>
            <button onClick={() => onTabChange('terms')} className={`pb-4 px-2 font-montserrat font-bold text-sm md:text-base transition-colors relative ${activeTab === 'terms' ? 'text-brand-orange' : 'text-zinc-500 hover:text-zinc-800'}`} data-id="element-93">
              Terms & Conditions
              {activeTab === 'terms' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-orange rounded-t-full" data-id="element-94" />}
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 font-opensans text-zinc-700" data-id="element-95">
          {activeTab === 'privacy' ? <div className="space-y-6 animate-in fade-in duration-300" data-id="element-96">
              <div data-id="element-97">
                <h2 className="text-2xl font-montserrat font-extrabold text-black mb-2" data-id="element-98">
                  Privacy Policy
                </h2>
                <p className="text-sm text-zinc-500 italic" data-id="element-99">
                  Last updated: March 2026
                </p>
              </div>

              <section data-id="element-100">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-101">
                  Information We Collect
                </h3>
                <p className="leading-relaxed" data-id="element-102">
                  We collect information you provide directly to us when you
                  make a purchase, such as your name, email address, and payment
                  information. Payment details are processed securely by our
                  third-party payment processors and are not stored on our
                  servers.
                </p>
              </section>

              <section data-id="element-103">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-104">
                  How We Use Your Information
                </h3>
                <p className="leading-relaxed" data-id="element-105">
                  We use the information we collect to deliver your digital
                  products, send important updates regarding your purchase,
                  provide customer support, and communicate with you about
                  related products or services that may interest you.
                </p>
              </section>

              <section data-id="element-106">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-107">
                  Data Security
                </h3>
                <p className="leading-relaxed" data-id="element-108">
                  We implement industry-standard security measures to protect
                  your personal information from unauthorized access,
                  alteration, disclosure, or destruction. However, no method of
                  transmission over the Internet is 100% secure.
                </p>
              </section>

              <section data-id="element-109">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-110">
                  Third-Party Services
                </h3>
                <p className="leading-relaxed" data-id="element-111">
                  We may share your information with trusted third-party service
                  providers who assist us in operating our website, conducting
                  our business, or servicing you, so long as those parties agree
                  to keep this information confidential.
                </p>
              </section>

              <section data-id="element-112">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-113">
                  Your Rights
                </h3>
                <p className="leading-relaxed" data-id="element-114">
                  You have the right to access, correct, or delete your personal
                  information at any time. You may also opt-out of receiving
                  promotional communications from us by following the
                  unsubscribe instructions included in those emails.
                </p>
              </section>

              <section data-id="element-115">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-116">
                  Contact Us
                </h3>
                <p className="leading-relaxed" data-id="element-117">
                  If you have any questions about this Privacy Policy, please
                  contact us at{' '}
                  <a href="mailto:support@urbanautonomysystem.com" className="text-brand-orange hover:underline" data-id="element-118">
                    support@urbanautonomysystem.com
                  </a>
                  .
                </p>
              </section>
            </div> : <div className="space-y-6 animate-in fade-in duration-300" data-id="element-119">
              <div data-id="element-120">
                <h2 className="text-2xl font-montserrat font-extrabold text-black mb-2" data-id="element-121">
                  Terms & Conditions
                </h2>
                <p className="text-sm text-zinc-500 italic" data-id="element-122">
                  Last updated: March 2026
                </p>
              </div>

              <section data-id="element-123">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-124">
                  Acceptance of Terms
                </h3>
                <p className="leading-relaxed" data-id="element-125">
                  By accessing and purchasing The Urban Autonomy System, you
                  accept and agree to be bound by the terms and provision of
                  this agreement. If you do not agree to abide by these terms,
                  please do not use this service.
                </p>
              </section>

              <section data-id="element-126">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-127">
                  Digital Product License
                </h3>
                <p className="leading-relaxed" data-id="element-128">
                  Upon purchase, you are granted a revocable, non-exclusive,
                  non-transferable, limited license to download, install, and
                  use the digital materials strictly in accordance with the
                  terms of this Agreement. You may not distribute, sell, or
                  share these materials with unauthorized third parties.
                </p>
              </section>

              <section data-id="element-129">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-130">
                  Refund Policy
                </h3>
                <p className="leading-relaxed" data-id="element-131">
                  We offer a 60-day money-back guarantee. If you are not
                  completely satisfied with your purchase, you may request a
                  full refund within 60 days of the original purchase date by
                  contacting our support team.
                </p>
              </section>

              <section data-id="element-132">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-133">
                  Disclaimer of Warranties
                </h3>
                <p className="leading-relaxed" data-id="element-134">
                  The information provided in this system is for educational and
                  informational purposes only. We make no warranties, expressed
                  or implied, regarding the completeness, accuracy, reliability,
                  or suitability of the information. Your use of the strategies
                  outlined is strictly at your own risk.
                </p>
              </section>

              <section data-id="element-135">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-136">
                  Limitation of Liability
                </h3>
                <p className="leading-relaxed" data-id="element-137">
                  In no event shall we be liable for any direct, indirect,
                  incidental, consequential, or special damages arising out of
                  or in any way connected with your use of our products or
                  services. Our maximum liability shall not exceed the purchase
                  price of the product.
                </p>
              </section>

              <section data-id="element-138">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-139">
                  Governing Law
                </h3>
                <p className="leading-relaxed" data-id="element-140">
                  These terms and conditions are governed by and construed in
                  accordance with the laws of the United States, and you
                  irrevocably submit to the exclusive jurisdiction of the courts
                  in that location.
                </p>
              </section>

              <section data-id="element-141">
                <h3 className="text-lg font-montserrat font-bold text-black mb-2" data-id="element-142">
                  Contact
                </h3>
                <p className="leading-relaxed" data-id="element-143">
                  For any questions regarding these Terms & Conditions, please
                  contact us at{' '}
                  <a href="mailto:support@urbanautonomysystem.com" className="text-brand-orange hover:underline" data-id="element-144">
                    support@urbanautonomysystem.com
                  </a>
                  .
                </p>
              </section>
            </div>}
        </div>
      </div>
    </div>;
}